import { useCallback, useMemo, useState } from 'react';
import { CATALOGUE } from '../data/catalogue';
import { errorMessageForCategory, statusByCategory } from './layerStatusAggregate';

/**
 * Every layer starts OFF, and this function is the only place that decides it.
 *
 * The map opens as plain satellite imagery with the panel closed. The layers are
 * there to be reached for, not to greet a farmer with a screenful of overlays
 * they did not ask for and have to work out how to remove.
 *
 * Session-only: nothing is persisted or restored, so opening the app is always
 * the same clean starting point.
 *
 * Every catalogue source used to carry a `defaultVisible: false` alongside this,
 * on all 24 of them, and NOTHING read it. It was removed on 24/08/2026 rather
 * than wired up, because it looked authoritative while being inert: anyone
 * setting one to `true` would have got no effect at all. If a per-layer default
 * is ever genuinely wanted, wire it here rather than reintroducing a field that
 * only appears to work.
 */
function buildInitialState() {
  return CATALOGUE.reduce((states, category) => {
    states[category.id] = {
      visible: false,
      status: 'idle',
    };
    return states;
  }, {});
}

const IDLE = { visible: false, status: 'idle', errorMessage: undefined };

/**
 * Toggle one layer, enforcing the single-layer rule: turning a layer on turns
 * every other layer off. Two fills stacked on satellite imagery are unreadable
 * whichever two they are, so there is no combination worth allowing.
 *
 * Pure and exported for the same reason as reconcileOverlays — the rule is worth
 * testing without mounting React.
 *
 * @param {Record<string, {visible: boolean, status: string}>} states
 * @param {string} id  category id being toggled
 * @returns {Record<string, object>} next states (the same object if id is unknown)
 */
export function applyToggle(states, id) {
  const target = states[id];
  if (!target) return states;

  const turningOn = !target.visible;
  const next = {};

  for (const [categoryId, state] of Object.entries(states)) {
    next[categoryId] =
      categoryId === id
        ? { ...state, ...IDLE, visible: turningOn }
        : { ...state, ...IDLE };
  }

  return next;
}

export function useMapLayers() {
  const [visibility, setVisibility] = useState(buildInitialState);
  // sourceId -> {status, errorMessage}. Health is PER SOURCE and rolled up for
  // display, because a category can hold several sources and they can disagree.
  // It used to be stored per category, last writer wins, which let Scotland's
  // dead 1:25k soil source report a failure over the 250k layer that had already
  // drawn. See lib/layerStatusAggregate.js.
  const [sourceStatuses, setSourceStatuses] = useState({});

  const toggle = useCallback((id) => {
    setVisibility((current) => applyToggle(current, id));
    // The single-layer rule means at most one category is ever on, so any toggle
    // invalidates every stored report. Clearing them is also what stops a status
    // from a previous viewing surviving onto the next one.
    setSourceStatuses((current) => (Object.keys(current).length ? {} : current));
  }, []);

  const setStatus = useCallback((sourceId, status, errorMessage) => {
    setSourceStatuses((current) => {
      const previous = current[sourceId];
      if (previous?.status === status && previous?.errorMessage === errorMessage) {
        return current;
      }
      return { ...current, [sourceId]: { status, errorMessage } };
    });
  }, []);

  const layerStates = useMemo(() => {
    const aggregated = statusByCategory(CATALOGUE, sourceStatuses);
    const states = {};
    for (const category of CATALOGUE) {
      const status = aggregated[category.id];
      states[category.id] = {
        ...visibility[category.id],
        status,
        errorMessage: errorMessageForCategory(category, sourceStatuses, status),
      };
    }
    return states;
  }, [visibility, sourceStatuses]);

  return { layerStates, toggle, setStatus };
}
